<!-- ToastNotification.vue -->
<template>
  <Teleport to="body">
    <div
      :class="[
        'fixed z-[60] pointer-events-none',
        positionClasses[config.position || 'top-right'],
      ]"
    >
      <TransitionGroup
        :name="config.animation || 'toast'"
        tag="div"
        class="space-y-2"
      >
        <div
          v-for="toast in toasts"
          :key="toast.id"
          :class="[
            'w-full max-w-sm pointer-events-auto relative',
            'bg-white rounded-lg shadow-lg border',
            'transform transition-all duration-300 ease-in-out',
            toast.className || '',
            borderClasses[toast.type],
            {
              'cursor-pointer': toast.closeOnClick,
            },
          ]"
          @click="handleToastClick(toast)"
          @mouseenter="handleMouseEnter(toast)"
          @mouseleave="handleMouseLeave(toast)"
        >
          <!-- Progress bar -->
          <div
            v-if="toast.showProgress && !toast.persistent"
            class="absolute top-0 left-0 h-1 transition-all duration-300"
            :class="progressBarClasses[toast.type]"
            :style="{ width: getProgressWidth(toast) }"
          ></div>

          <!-- Toast content -->
          <div class="p-4">
            <div class="flex items-center gap-3">
              <!-- Icon -->
              <div
                v-if="!toast.hideIcon"
                :class="[
                  'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
                  iconBgClasses[toast.type],
                  { 'animate-spin': toast.type === 'loading' }
                ]"
              >
                <component
                  v-if="toast.customIcon"
                  :is="toast.customIcon"
                  class="w-6 h-6 text-white"
                />
                <span
                  v-else
                  class="text-xl font-bold text-white"
                  v-html="getToastIcon(toast)"
                ></span>
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <!-- Title -->
                <div
                  v-if="toast.title"
                  class="text-sm font-semibold text-gray-900 mb-1"
                >
                  {{ toast.title }}
                </div>

                <!-- Message -->
                <div
                  class="text-sm text-gray-700"
                  :class="{ 'font-medium': !toast.title }"
                >
                  <div v-if="toast.html" v-html="toast.message"></div>
                  <div v-else>{{ toast.message }}</div>
                </div>

                <!-- Actions -->
                <div
                  v-if="toast.actions && toast.actions.length > 0"
                  class="mt-3 flex flex-wrap gap-2"
                >
                  <button
                    v-for="(action, index) in toast.actions"
                    :key="index"
                    @click="handleAction(toast, action)"
                    :class="[
                      'text-xs font-medium px-3 py-1 rounded transition-colors',
                      action.style === 'primary'
                        ? actionButtonClasses[toast.type]
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100',
                    ]"
                  >
                    {{ action.label }}
                  </button>
                </div>
              </div>

              <!-- Close button -->
              <button
                v-if="toast.dismissible !== false"
                @click.stop="removeToast(toast.id)"
                class="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                :aria-label="`Dismiss ${toast.type} notification`"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Visual timer -->
          <div
            v-if="showVisualTimer && toast.duration > 0 && !toast.persistent"
            class="absolute bottom-0 left-0 h-1 bg-gray-200"
          >
            <div
              :class="[
                'h-full transition-all ease-linear',
                progressBarClasses[toast.type],
              ]"
              :style="{
                width: getTimerWidth(toast),
                transitionDuration: isToastPaused(toast.id)
                  ? '0ms'
                  : `${getRemainingTime(toast)}ms`,
              }"
            ></div>
          </div>

        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useToast } from '@/composables/useToast';

// Props
const props = defineProps({
  showVisualTimer: {
    type: Boolean,
    default: false,
  },
  maxWidth: {
    type: String,
    default: 'max-w-sm',
  },
});

// Composable
const { toasts, config, removeToast, pauseToast, resumeToast, isToastPaused } =
  useToast();

// Timer management
const timerStartTimes = ref(new Map());
const progressTimers = ref(new Map());

// Position classes - Adjusted for navigation bar
const positionClasses = {
  'top-left': 'top-20 left-4 sm:left-6',
  'top-center': 'top-20 left-1/2 transform -translate-x-1/2',
  'top-right': 'top-20 right-4 sm:right-6',
  'bottom-left': 'bottom-4 left-4 sm:left-6',
  'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2',
  'bottom-right': 'bottom-4 right-4 sm:right-6',
};

// Styling classes
const iconBgClasses = {
  success: 'bg-green-500',
  error: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-blue-500',
  loading: 'bg-blue-500',
};

const borderClasses = {
  success: 'border-green-200',
  error: 'border-red-200',
  warning: 'border-amber-200',
  info: 'border-blue-200',
  loading: 'border-blue-200',
};

const progressBarClasses = {
  success: 'bg-green-500',
  error: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-blue-500',
  loading: 'bg-blue-500',
};

const actionButtonClasses = {
  success: 'text-green-700 hover:text-green-800 bg-green-50 hover:bg-green-100',
  error: 'text-red-700 hover:text-red-800 bg-red-50 hover:bg-red-100',
  warning: 'text-amber-700 hover:text-amber-800 bg-amber-50 hover:bg-amber-100',
  info: 'text-blue-700 hover:text-blue-800 bg-blue-50 hover:bg-blue-100',
  loading: 'text-blue-700 hover:text-blue-800 bg-blue-50 hover:bg-blue-100',
};

// Icon mapping - Better visual icons
const defaultIcons = {
  success: '✓',
  error: '✕',
  warning: '!',
  info: 'i',
  loading: '↻',
};

// Methods
const getToastIcon = toast => {
  if (toast.icon) return toast.icon;
  return defaultIcons[toast.type] || defaultIcons.info;
};

const getProgressWidth = toast => {
  if (toast.progress !== undefined) {
    return `${Math.min(100, Math.max(0, toast.progress))}%`;
  }
  return '0%';
};

const getRemainingTime = toast => {
  if (!timerStartTimes.value.has(toast.id)) {
    timerStartTimes.value.set(toast.id, Date.now());
    return toast.duration;
  }

  const startTime = timerStartTimes.value.get(toast.id);
  const elapsed = Date.now() - startTime;
  return Math.max(0, toast.duration - elapsed);
};

const getTimerWidth = toast => {
  if (isToastPaused(toast.id)) {
    return '100%';
  }

  const remaining = getRemainingTime(toast);
  const progress = Math.max(0, (remaining / toast.duration) * 100);
  return `${progress}%`;
};

const handleToastClick = toast => {
  if (toast.closeOnClick) {
    removeToast(toast.id);
  }

  if (toast.onClick) {
    toast.onClick(toast);
  }
};

const handleMouseEnter = toast => {
  if (toast.pauseOnHover !== false && config.pauseOnHover) {
    pauseToast(toast.id);
  }
};

const handleMouseLeave = toast => {
  if (toast.pauseOnHover !== false && config.pauseOnHover) {
    resumeToast(toast.id);
  }
};

const handleAction = (toast, action) => {
  if (action.handler) {
    action.handler(toast, action);
  }

  if (action.closeOnClick !== false) {
    removeToast(toast.id);
  }
};

// Keyboard support
const handleKeyPress = event => {
  // Escape key closes all dismissible toasts
  if (event.key === 'Escape') {
    const dismissibleToasts = toasts.value.filter(t => t.dismissible !== false);
    dismissibleToasts.forEach(toast => removeToast(toast.id));
  }
};

// Clean up timers when toasts are removed
watch(
  toasts,
  (newToasts, oldToasts) => {
    if (oldToasts) {
      const removedToastIds = oldToasts
        .filter(
          oldToast => !newToasts.find(newToast => newToast.id === oldToast.id)
        )
        .map(toast => toast.id);

      removedToastIds.forEach(id => {
        timerStartTimes.value.delete(id);
        if (progressTimers.value.has(id)) {
          clearInterval(progressTimers.value.get(id));
          progressTimers.value.delete(id);
        }
      });
    }
  },
  { deep: true }
);

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('keydown', handleKeyPress);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyPress);

  // Clean up all timers
  timerStartTimes.value.clear();
  progressTimers.value.forEach(timer => clearInterval(timer));
  progressTimers.value.clear();
});
</script>

<style scoped>
/* Toast animations - Smooth slide from right */
.toast-enter-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.9);
}

.toast-enter-to {
  opacity: 1;
  transform: translateX(0) scale(1);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(120%) scale(0.95);
}

.toast-move {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Slide animation */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* Fade animation */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Scale animation */
.scale-enter-active,
.scale-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.scale-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Loading animation */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Responsive design */
@media (max-width: 640px) {
  /* Ensure toasts don't overflow on small screens */
  .max-w-sm {
    max-width: calc(100vw - 3rem);
    margin: 0 0.5rem;
  }
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .toast-enter-active,
  .toast-leave-active,
  .toast-move,
  .slide-enter-active,
  .slide-leave-active,
  .fade-enter-active,
  .fade-leave-active,
  .scale-enter-active,
  .scale-leave-active {
    transition: none;
  }

  .animate-spin {
    animation: none;
  }
}

@media (prefers-contrast: high) {
  .bg-white {
    border: 2px solid #000;
  }
}

/* Focus styles for better accessibility */
button:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .bg-white {
    background-color: #1f2937;
  }

  .text-gray-900 {
    color: #f9fafb;
  }

  .text-gray-700 {
    color: #d1d5db;
  }

  .text-gray-400 {
    color: #9ca3af;
  }

  .border-gray-200 {
    border-color: #374151;
  }
}
</style>
